/**
 * Flatten a transcript to plain text, optionally restricted to a time window.
 * The narrow shape of the segment parameter keeps this usable against a partial
 * manifest, which is what the ingest pipeline has on hand mid-flight.
 */
export function transcriptText(segments, window) {
    const selected = window
        ? segments.filter((s) => s.startSec < window.endSec && window.startSec < s.endSec)
        : segments;
    return selected
        .map((s) => s.text.trim())
        .filter((text) => text.length > 0)
        .join(" ");
}
/**
 * Whether an asset is waiting for bytes that can never arrive (ADR 000054).
 *
 * The mirror of `domain.Asset.UploadAbandoned`. Not "probably abandoned": the
 * only URL the asset was ever given has expired, so nothing can be PUT under
 * it and no other state is reachable.
 *
 * Derived here rather than served as a field, for the same reason a segment's
 * state is derived from the rendition it points at. A stored copy is one more
 * thing that can disagree with the truth, and the way anybody finds out is the
 * wrong thing on screen.
 */
export function uploadAbandoned(asset, now = new Date()) {
    // An import was never waiting for an upload, so an absent expiry means "not
    // applicable" rather than "expired long ago".
    if (!asset.uploadExpiresAt)
        return false;
    return asset.status === "pending" && now.getTime() > Date.parse(asset.uploadExpiresAt);
}
/**
 * Whether the last re-analysis of this asset gave up and left it as it was
 * (ADR 000120).
 *
 * A re-analysis that runs out of attempts puts the asset back to `ready` with
 * its manifest untouched and the reason recorded, because breaking a working
 * asset while trying to improve it is worse than not improving it
 * (ADR 000063). That pair of facts is the whole signal: no other path leaves a
 * reason on a settled asset, since every write that settles one either clears
 * the field or is the failure the badge beside it already reports, and the two
 * paths that record a reason without touching the status leave the asset
 * `analyzing`.
 *
 * Derived rather than served, the same as `uploadAbandoned`: the fields are
 * already in the response, and a stored copy is one more thing that can
 * disagree with them.
 */
export function reanalysisGaveUp(asset) {
    return asset.status === "ready" && Boolean(asset.failureReason);
}
//# sourceMappingURL=asset.js.map